// @flow
import { createSelector } from 'reselect';

import {
  selectIsLoadedInfos as selectIsLoadedCurrency,
  selectCurrency,
} from '@gandi/billing';
import { selectOneOrganization } from '@gandi/redux-organization';
import {
  selectIsMandatorySubdivision,
  selectIsMandatoryZipCode,
} from '@gandi/redux-places';
import {
  selectCurrent,
  selectCurrentId,
  type Organization,
} from '@gandi/redux-sharing';

import type { FullState } from '../../types';

const orgFields = [
  'type',
  'firstname',
  'lastname',
  'country',
  'city',
  'email',
  'bu_id',
  'phone',
];

declare function selectShopCurrent(
  state: FullState,
  isAuthenticated: true,
): Organization;
// eslint-disable-next-line no-redeclare
declare function selectShopCurrent(
  state: FullState,
  isAuthenticated: false,
): void | Organization;

/* If user is authenticated then selectCurrent returns an organization, but it may not be true when he is not */
// eslint-disable-next-line no-redeclare
export function selectShopCurrent(state: FullState, isAuthenticated: boolean) {
  const currentOrg =
    selectCurrent(state) ||
    // selectCurrent from redux-sharing should always be filled when authenticated
    // however when user switch orga through the preferences dialog
    // selectCurrent does not find the freshly selected organization
    // in this case, the organization should be find from redux-organization instead of redux-sharing
    selectOneOrganization(state, selectCurrentId(state));

  return isAuthenticated ? currentOrg : currentOrg || undefined;
}

export const selectCurrentBU = (state: FullState): void | number =>
  selectShopCurrent(state, false)?.bu_id;
/**
 * We need to check that all of the target organization's info is provided
 * before we use this info to generate domain contacts, allow payment, etc...
 * Return the list of missing fields if any.
 * @private
 */
// FIXME: MEMOIZE
const selectMissingOrgInfo = (
  state: FullState,
  orgId: string,
): Array<string> | void => {
  const currentId = selectCurrentId(state);
  if (!currentId) {
    return undefined;
  }

  const org =
    currentId === orgId
      ? selectShopCurrent(state, false)
      : selectOneOrganization(state, orgId);

  if (!org) {
    return undefined;
  }

  const missingOrgFields = orgFields.filter(field => !org[field]);

  if (org.type && org.type !== 'individual' && !org.orgname) {
    missingOrgFields.push('orgname');
  }

  const currencyProps = { sharingId: orgId };
  // $FlowFixMe[incompatible-call]
  const loadedCurrency = selectIsLoadedCurrency(state, currencyProps);
  // $FlowFixMe[incompatible-call]
  const currency = selectCurrency(state, currencyProps);
  if (loadedCurrency && !currency) {
    missingOrgFields.push('currency');
  }

  if (org.country) {
    const mandatorySubdivision = selectIsMandatorySubdivision(
      state,
      org.country,
    );
    const mandatoryZipCode = selectIsMandatoryZipCode(state, org.country);

    if (mandatorySubdivision && !org.state) {
      missingOrgFields.push('state');
    }

    if (mandatoryZipCode && !org.zip) {
      missingOrgFields.push('zip');
    }
  }

  return missingOrgFields.length > 0 ? missingOrgFields : undefined;
};

/**
 * We need to check that all of the target organization's info is provided
 * before we use this info to generate domain contacts, allow payment, etc...
 * Return `undefined` if user is unauthenticated or current sharingId organization info is unavailable.
 * Return `true` if missing organization info
 * Return `false` if apparently not missing organization info
 */
export const selectHasMissingOrgInfo: (
  state: FullState,
  orgId: string,
) => boolean | void = createSelector([selectMissingOrgInfo], missingOrgInfo => {
  if (!Array.isArray(missingOrgInfo)) {
    return undefined;
  }

  return missingOrgInfo.length > 0;
});
